import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, CreditCard, Download, ArrowRight, Search, Shield, Zap, Sparkles, Star, FileText, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { FileCard } from '../components/FileCard';
import { Footer } from '../components/Footer';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-50px'
  },
  transition: {
    duration: 0.5
  }
};
const stagger = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function Landing() {
  const {
    t
  } = useLanguage();
  const {
    files
  } = useData();
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const featured = files.slice(0, 4);
  return <div className="w-full min-h-screen flex flex-col" data-id="element-771">
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[420px] sm:min-h-[480px] flex items-center" data-id="element-772">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1920&q=80)'
      }} data-id="element-773" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/95 via-purple-900/85 to-blue-950/80" data-id="element-774" />

        {/* Floating icons */}
        <motion.div animate={{
        y: [0, -12, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity
      }} className="absolute right-[8%] top-[15%] hidden lg:block" data-id="element-775">
          <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20" data-id="element-776">
            <GraduationCap className="h-8 w-8 text-white/70" data-id="element-777" />
          </div>
        </motion.div>
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        delay: 1
      }} className="absolute right-[22%] bottom-[18%] hidden lg:block" data-id="element-778">
          <div className="h-14 w-14 rounded-xl bg-purple-400/15 backdrop-blur-sm flex items-center justify-center border border-purple-400/25" data-id="element-779">
            <Download className="h-7 w-7 text-purple-300" data-id="element-780" />
          </div>
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24 w-full" data-id="element-781">
          <div className="max-w-2xl" data-id="element-782">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-783">
              <motion.div initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.3,
              duration: 0.5
            }} className="flex h-1 w-16 rounded-full overflow-hidden mb-5 origin-left" data-id="element-784">
                <div className="flex-1 bg-blue-400" data-id="element-785" />
                <div className="flex-1 bg-yellow-400" data-id="element-786" />
                <div className="flex-1 bg-green-400" data-id="element-787" />
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold leading-tight mb-3 sm:mb-4 text-white" data-id="element-788">
                {t('landing.hero.title')}
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.35
            }} className="text-sm sm:text-base md:text-lg text-blue-100/90 mb-6 sm:mb-8 max-w-lg" data-id="element-789">
                {t('landing.hero.subtitle')}
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="flex flex-wrap gap-2 sm:gap-3" data-id="element-790">
                <Link to="/marketplace" data-id="element-791">
                  <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 gap-2 font-semibold shadow-lg text-sm sm:text-base h-10 sm:h-11" data-id="element-792">
                    <Search className="h-4 w-4" data-id="element-793" />
                    {t('landing.hero.browse')}
                  </Button>
                </Link>
                {!currentUser && <Link to="/auth" data-id="element-794">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2 text-sm sm:text-base h-10 sm:h-11" data-id="element-795">
                      {t('landing.hero.signup')}
                      <ArrowRight className="h-4 w-4" data-id="element-796" />
                    </Button>
                  </Link>}
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.7
            }} className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 sm:mt-8 text-blue-200/80 text-[10px] sm:text-xs" data-id="element-797">
                <span className="flex items-center gap-1" data-id="element-798">
                  <Shield className="h-3 w-3" data-id="element-799" /> Secure
                </span>
                <span className="flex items-center gap-1" data-id="element-800">
                  <Zap className="h-3 w-3" data-id="element-801" /> Instant
                </span>
                <span className="flex items-center gap-1" data-id="element-802">
                  <GraduationCap className="h-3 w-3" data-id="element-803" /> Quality
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-12 sm:py-16 bg-background" data-id="element-804">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8" data-id="element-805">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 sm:mb-12" data-id="element-806">
            <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4" data-id="element-807">
              {t('landing.about.title')}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground" data-id="element-808">
              {t('landing.about.text')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-muted/30" data-id="element-809">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8" data-id="element-810">
          <motion.h2 {...fadeUp} className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-811">
            {t('landing.how.title')}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8" data-id="element-812">
            {[{
            icon: Search,
            title: t('landing.how.step1.title'),
            text: t('landing.how.step1.text'),
            color: 'bg-blue-500/10 text-blue-500'
          }, {
            icon: CreditCard,
            title: t('landing.how.step2.title'),
            text: t('landing.how.step2.text'),
            color: 'bg-yellow-500/10 text-yellow-500'
          }, {
            icon: Download,
            title: t('landing.how.step3.title'),
            text: t('landing.how.step3.text'),
            color: 'bg-green-500/10 text-green-500'
          }].map((step, i) => <motion.div key={i} {...stagger} transition={{
            delay: i * 0.15
          }} data-id="element-813">
                <Card className="text-center p-4 sm:p-6 h-full hover:shadow-lg transition-shadow" data-id="element-814">
                  <CardContent className="p-0 flex flex-col items-center gap-3 sm:gap-4" data-id="element-815">
                    <motion.div whileHover={{
                  scale: 1.1,
                  rotate: 5
                }} className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full flex items-center justify-center ${step.color}`} data-id="element-816">
                      <step.icon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-817" />
                    </motion.div>
                    <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-xs sm:text-sm text-primary" data-id="element-818">
                      {i + 1}
                    </div>
                    <h3 className="font-heading font-semibold text-base sm:text-lg" data-id="element-819">
                      {step.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground" data-id="element-820">
                      {step.text}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && <section className="py-12 sm:py-16 bg-background" data-id="element-821">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8" data-id="element-822">
            <motion.div {...fadeUp} className="flex items-center justify-between mb-6 sm:mb-8" data-id="element-823">
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold" data-id="element-824">
                {t('landing.featured')}
              </h2>
              <Link to="/marketplace" data-id="element-825">
                <Button variant="ghost" size="sm" className="gap-1 text-xs sm:text-sm" data-id="element-826">
                  {t('nav.marketplace')}{' '}
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" data-id="element-827" />
                </Button>
              </Link>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-828">
              {featured.map(file => <FileCard key={file.id} file={file} purchased={false} onBuy={() => navigate(currentUser ? `/payment/${file.id}` : '/auth')} data-id="element-829" />)}
            </div>
          </div>
        </section>}

      {/* Empty state when no files */}
      {featured.length === 0 && <section className="py-16 sm:py-20 bg-background" data-id="element-830">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 text-center" data-id="element-831">
            <motion.div {...fadeUp} className="max-w-md mx-auto" data-id="element-832">
              <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity
          }} data-id="element-833">
                <FileText className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" data-id="element-834" />
              </motion.div>
              <h3 className="font-heading text-lg font-semibold mb-2" data-id="element-835">
                No materials yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4" data-id="element-836">
                The admin will upload educational materials soon. Check back
                later!
              </p>
              <Link to="/chat" data-id="element-837">
                <Button variant="outline" size="sm" className="gap-2" data-id="element-838">
                  Contact Admin <ArrowRight className="h-3 w-3" data-id="element-839" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>}

      {/* Features */}
      <section className="py-12 sm:py-16 bg-muted/30" data-id="element-840">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8" data-id="element-841">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-842">
            {[{
            icon: Shield,
            title: 'Secure Payments',
            desc: 'Pay safely via MTN Mobile Money'
          }, {
            icon: Zap,
            title: 'Instant Access',
            desc: 'Download immediately after verification'
          }, {
            icon: GraduationCap,
            title: 'Quality Content',
            desc: 'Curated educational materials'
          }].map((feat, i) => <motion.div key={i} {...stagger} transition={{
            delay: i * 0.1
          }} data-id="element-843">
                <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl hover:bg-card transition-colors" data-id="element-844">
                  <motion.div whileHover={{
                scale: 1.1
              }} className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-845">
                    <feat.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" data-id="element-846" />
                  </motion.div>
                  <div data-id="element-847">
                    <h3 className="font-semibold text-sm sm:text-base mb-0.5" data-id="element-848">
                      {feat.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground" data-id="element-849">
                      {feat.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-850" />
    </div>;
}